import _extends from "@babel/runtime/helpers/extends";
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { alto, apple, bondiBlue, boulder, catskillWhite, dustGray, fairPink, mercury, mineShaft, thunderBird, white, willowBrook } from '@parchment/atoms';
import { CONTAINER_SIZES, SCALE, VALIDATION_STATES } from '../../utils/constants';
import Skeleton from 'react-loading-skeleton';
import { SecureIcon, ValidationRequiredIcon, ValidIcon, WarningIcon } from '@parchment/icons';
const TextField = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    autoComplete,
    required = false,
    visited: visitedProp = false,
    onFocus,
    onKeyDown,
    onChange = (val, e) => undefined,
    onBlur = e => undefined,
    type = 'text',
    scale = SCALE.medium,
    valid = VALIDATION_STATES.neutral,
    disabled = false,
    name,
    label,
    placeholder,
    showRequiredInPlaceholder = false,
    className,
    style,
    loading = false,
    step,
    ...props
  } = _ref;
  const [visited, setVisited] = useState(!!visitedProp);
  const [value, setValue] = useState(props.value);
  const [validation, setValidation] = useState(VALIDATION_STATES.neutral);
  const [isFocused, setIsFocused] = useState(false);
  useEffect(() => setVisited(!!visitedProp), [visitedProp]);
  useEffect(() => setValidation(valid), [valid]);
  useEffect(() => {
    setValue(props.value || '');
    if (props.value || visited) {
      onBlur && onBlur();
    }
  }, [props.value]);
  const handleBlur = e => {
    setIsFocused(false);
    const newValue = (value === null || value === void 0 ? void 0 : value.trim()) || '';
    setValue(newValue);
    onBlur && onBlur(e);
  };
  const renderIcon = () => {
    return /*#__PURE__*/React.createElement(React.Fragment, null, disabled && /*#__PURE__*/React.createElement(SecureIcon, {
      contextType: "image",
      color: boulder
    }), !disabled && /*#__PURE__*/React.createElement(React.Fragment, null, required && showRequiredInPlaceholder && [VALIDATION_STATES.neutral, VALIDATION_STATES.pending].includes(validation) && !isFocused ? /*#__PURE__*/React.createElement(ValidationRequiredIcon, {
      contextType: "image",
      color: thunderBird
    }) : validation === VALIDATION_STATES.success ? /*#__PURE__*/React.createElement(ValidIcon, {
      contextType: "image",
      color: apple
    }) : validation === VALIDATION_STATES.error ? /*#__PURE__*/React.createElement(WarningIcon, {
      contextType: "image",
      color: thunderBird
    }) : null));
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, loading ? /*#__PURE__*/React.createElement("div", {
    style: {
      lineHeight: 0,
      marginTop: '-1px'
    }
  }, /*#__PURE__*/React.createElement(Skeleton, {
    height: CONTAINER_SIZES[scale]
  })) : /*#__PURE__*/React.createElement(FieldWrapper, {
    scale: scale,
    "data-visited": visited,
    "data-valid": valid,
    "data-required": required,
    disabled: disabled,
    showRequiredInPlaceholder: showRequiredInPlaceholder,
    className: className,
    style: style,
    isFocused: isFocused,
    ref: ref
  }, renderIcon(), /*#__PURE__*/React.createElement(StyledInput, _extends({}, props, {
    autoComplete: autoComplete,
    type: type,
    value: value ? value : disabled ? 'n/a' : '',
    scale: scale,
    "data-visited": visited,
    "data-required": required,
    "data-valid": validation,
    name: name,
    onChange: e => {
      setValue(e.currentTarget.value || '');
      onChange && onChange(e.currentTarget.value, e);
    },
    onBlur: e => handleBlur(e),
    onFocus: e => {
      setVisited(true);
      setIsFocused(true);
      onFocus && onFocus(e);
    },
    onKeyDown: e => onKeyDown && onKeyDown(e),
    placeholder: placeholder,
    disabled: disabled,
    step: step
  }))));
});
export default TextField;
TextField.displayName = 'TextField';
const BORDER_RADIUS = {
  small: '4px',
  medium: '5px',
  large: '6px'
};
const FieldWrapper = /*#__PURE__*/styled.div.withConfig({
  displayName: "TextField__FieldWrapper",
  componentId: "sc-1hx1ftq-0"
})(["padding:", ";background-color:", ";display:flex;align-items:center;font-weight:600;color:", ";box-shadow:inset 0 0 0 1px ", ";border-radius:", ";transition:all 100ms ease-out;cursor:pointer;svg{margin-right:8px;flex-shrink:0;}input{padding:0;}input[type=\"search\"]::-webkit-search-cancel-button{appearance:auto;cursor:pointer;}&.gc-top,&.gc-right,&.gc-bottom,&.gc-left,&.gc-none{border-width:0;margin-top:-1px;margin-left:-1px;}.gc-top &,.gc-right &,.gc-bottom &,.gc-left &,.gc-none &{border-radius:0;margin-top:-1px;margin-left:-1px;}.gc-top.gc-left &,&.gc-top.gc-left{border-top-left-radius:", ";}.gc-top.gc-right &,&.gc-top.gc-right{border-top-right-radius:", ";}.gc-bottom.gc-left &,&.gc-bottom.gc-left{border-bottom-left-radius:", ";}.gc-bottom.gc-right &,&.gc-bottom.gc-right{border-bottom-right-radius:", ";}", " ", ";", " ", ""], props => props.isFocused && !props['data-visited'] ? '0' : '0 .875em', _ref2 => {
  var _theme$whiteSpace;
  let {
    theme
  } = _ref2;
  return (theme === null || theme === void 0 ? void 0 : theme.name) === 'dark' ? theme === null || theme === void 0 ? void 0 : (_theme$whiteSpace = theme.whiteSpace) === null || _theme$whiteSpace === void 0 ? void 0 : _theme$whiteSpace.zIndex3 : white;
}, _ref3 => {
  var _theme$fontColors, _theme$fontColors$bod;
  let {
    theme
  } = _ref3;
  return (theme === null || theme === void 0 ? void 0 : (_theme$fontColors = theme.fontColors) === null || _theme$fontColors === void 0 ? void 0 : (_theme$fontColors$bod = _theme$fontColors.body) === null || _theme$fontColors$bod === void 0 ? void 0 : _theme$fontColors$bod.strong) || mineShaft;
}, _ref4 => {
  let {
    theme
  } = _ref4;
  return (theme === null || theme === void 0 ? void 0 : theme.borderColor) || alto;
}, props => BORDER_RADIUS[props.scale], props => BORDER_RADIUS[props.scale], props => BORDER_RADIUS[props.scale], props => BORDER_RADIUS[props.scale], props => BORDER_RADIUS[props.scale], props => {
  var _props$theme;
  return props['data-valid'] === VALIDATION_STATES.neutral && props.isFocused && `
        background-color: ${((_props$theme = props.theme) === null || _props$theme === void 0 ? void 0 : _props$theme.name) === 'dark' ? 'unset' : catskillWhite};
        box-shadow: 0 0 5px 0px  ${bondiBlue};
    `;
}, props => {
  var _props$theme2, _props$theme3, _props$theme3$whiteSp, _props$theme4;
  return props['data-visited'] === true && props['data-valid'] === VALIDATION_STATES.success && `
        background-color: ${((_props$theme2 = props.theme) === null || _props$theme2 === void 0 ? void 0 : _props$theme2.name) === 'dark' ? (_props$theme3 = props.theme) === null || _props$theme3 === void 0 ? void 0 : (_props$theme3$whiteSp = _props$theme3.whiteSpace) === null || _props$theme3$whiteSp === void 0 ? void 0 : _props$theme3$whiteSp.zIndex3 : willowBrook};
        box-shadow: ${props.isFocused ? `0 0 5px 0  ${apple}, inset 50px 0 0 -43px ${apple}` : `inset 50px 0 0 -43px ${apple}, inset 0 0 0 1px ${((_props$theme4 = props.theme) === null || _props$theme4 === void 0 ? void 0 : _props$theme4.name) === 'dark' ? apple : alto}`}
        `;
}, props => {
  var _props$theme5, _props$theme6, _props$theme6$whiteSp, _props$theme7;
  return props['data-visited'] === true && props['data-valid'] === VALIDATION_STATES.error && `
        background-color: ${((_props$theme5 = props.theme) === null || _props$theme5 === void 0 ? void 0 : _props$theme5.name) === 'dark' ? (_props$theme6 = props.theme) === null || _props$theme6 === void 0 ? void 0 : (_props$theme6$whiteSp = _props$theme6.whiteSpace) === null || _props$theme6$whiteSp === void 0 ? void 0 : _props$theme6$whiteSp.zIndex3 : fairPink};
        box-shadow: ${props.isFocused ? `0 0 5px ${thunderBird}, ` : ''}inset 50px 0 0 -43px ${thunderBird}, inset 0 0 0 1px ${((_props$theme7 = props.theme) === null || _props$theme7 === void 0 ? void 0 : _props$theme7.name) === 'dark' ? thunderBird : alto};
        input {
            color: ${thunderBird};
        }
    `;
}, props => {
  var _props$theme8, _props$theme9, _props$theme10;
  return props.disabled && `
        background-color: ${((_props$theme8 = props.theme) === null || _props$theme8 === void 0 ? void 0 : _props$theme8.name) === 'dark' ? 'unset' : mercury};
        box-shadow: inset 50px 0 0 -43px ${((_props$theme9 = props.theme) === null || _props$theme9 === void 0 ? void 0 : _props$theme9.borderColor) || alto}, inset 0 0 0 1px ${((_props$theme10 = props.theme) === null || _props$theme10 === void 0 ? void 0 : _props$theme10.borderColor) || alto};
    `;
});
const StyledInput = /*#__PURE__*/styled.input.withConfig({
  displayName: "TextField__StyledInput",
  componentId: "sc-1hx1ftq-1"
})(["&:-webkit-autofill{transition:all 99999s;}box-sizing:border-box;background:unset;border-radius:", ";height:", ";font-size:", ";width:100%;border-style:none;font-weight:600;color:", ";::placeholder{font-weight:normal;color:", ";}:-ms-input-placeholder{font-weight:normal;color:", ";}::-ms-input-placeholder{font-weight:normal;color:", ";}:focus{outline:none;padding-left:", ";}", ""], _ref5 => {
  let {
    scale
  } = _ref5;
  return {
    small: '4px',
    medium: '5px',
    large: '6px'
  }[scale];
}, _ref6 => {
  let {
    scale
  } = _ref6;
  return {
    small: '40px',
    medium: '50px',
    large: '60px'
  }[scale];
}, _ref7 => {
  let {
    scale
  } = _ref7;
  return {
    small: '13px',
    medium: '16px',
    large: '20px'
  }[scale];
}, _ref8 => {
  var _theme$fontColors2, _theme$fontColors2$bo;
  let {
    theme
  } = _ref8;
  return (theme === null || theme === void 0 ? void 0 : (_theme$fontColors2 = theme.fontColors) === null || _theme$fontColors2 === void 0 ? void 0 : (_theme$fontColors2$bo = _theme$fontColors2.body) === null || _theme$fontColors2$bo === void 0 ? void 0 : _theme$fontColors2$bo.strong) || mineShaft;
}, _ref9 => {
  var _theme$fontColors3, _theme$fontColors3$bo;
  let {
    theme
  } = _ref9;
  return (theme === null || theme === void 0 ? void 0 : (_theme$fontColors3 = theme.fontColors) === null || _theme$fontColors3 === void 0 ? void 0 : (_theme$fontColors3$bo = _theme$fontColors3.body) === null || _theme$fontColors3$bo === void 0 ? void 0 : _theme$fontColors3$bo.weak) || boulder;
}, _ref10 => {
  var _theme$fontColors4, _theme$fontColors4$bo;
  let {
    theme
  } = _ref10;
  return (theme === null || theme === void 0 ? void 0 : (_theme$fontColors4 = theme.fontColors) === null || _theme$fontColors4 === void 0 ? void 0 : (_theme$fontColors4$bo = _theme$fontColors4.body) === null || _theme$fontColors4$bo === void 0 ? void 0 : _theme$fontColors4$bo.weak) || boulder;
}, _ref11 => {
  var _theme$fontColors5, _theme$fontColors5$bo;
  let {
    theme
  } = _ref11;
  return (theme === null || theme === void 0 ? void 0 : (_theme$fontColors5 = theme.fontColors) === null || _theme$fontColors5 === void 0 ? void 0 : (_theme$fontColors5$bo = _theme$fontColors5.body) === null || _theme$fontColors5$bo === void 0 ? void 0 : _theme$fontColors5$bo.weak) || boulder;
}, props => props['data-valid'] === VALIDATION_STATES.neutral && !props['data-visited'] && '10px', _ref12 => {
  let {
    disabled,
    value,
    theme
  } = _ref12;
  if (disabled && value === 'n/a') {
    var _theme$fontColors6, _theme$fontColors6$bo;
    return `
                color: ${(theme === null || theme === void 0 ? void 0 : (_theme$fontColors6 = theme.fontColors) === null || _theme$fontColors6 === void 0 ? void 0 : (_theme$fontColors6$bo = _theme$fontColors6.body) === null || _theme$fontColors6$bo === void 0 ? void 0 : _theme$fontColors6$bo.disabled) || dustGray};  
                background: transparent;
                cursor: not-allowed;
                font-style: italic;
                font-weight: 400;
            `;
  } else if (disabled) {
    return `
                color: ${boulder};
                background: transparent;
                cursor: not-allowed;
            `;
  }
});